import { useEffect, useMemo, useState } from "react";
import ApiDataAnalitic, {
  AnalyticsForbiddenError,
} from "../api/ApiDataAnalitic";
import RolesState from "../api/RolesState";
import User from "../api/User";
import { canViewEventTotals } from "../utils/eventTotalsSaUtils";
import { buildPrintLabelStats } from "../utils/printLabelStatsUtils";
import { formatIsoDateShortDisplay } from "../utils/subscriptionPeriodDate";

// TEMP/DEBUG: фиксированный январь 2026 для отладки калькулятора; вернуть getRolling30DayRange() после отладки.
const DEBUG_PRINT_LABEL_PERIOD = { from: "2026-01-01", to: "2026-01-31" };

export function useCompanyPrintLabelStats(companyId) {
  const canView = canViewEventTotals(RolesState, User.user);
  const debugRange = useMemo(() => DEBUG_PRINT_LABEL_PERIOD, []);
  const periodHint = useMemo(
    () =>
      `${formatIsoDateShortDisplay(debugRange.from)} — ${formatIsoDateShortDisplay(debugRange.to)}`,
    [debugRange.from, debugRange.to],
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [stats, setStats] = useState(null);

  const active = Boolean(companyId) && canView;

  useEffect(() => {
    if (!active) {
      setLoading(false);
      setError(false);
      setStats(null);
      return undefined;
    }

    const controller = new AbortController();
    const { signal } = controller;
    let ignore = false;

    setLoading(true);
    setError(false);
    setStats(null);

    (async () => {
      try {
        const message = await ApiDataAnalitic.fetchLabelStatisticSa({
          companyId,
          filterdate: [debugRange.from, debugRange.to],
          step: "d",
          event: "p",
          signal,
        });
        if (signal.aborted || ignore) {
          return;
        }
        if (message === undefined) {
          setError(true);
          setStats(null);
        } else {
          setStats(buildPrintLabelStats(message));
          setError(false);
        }
      } catch (e) {
        if (
          signal.aborted ||
          ignore ||
          e?.code === "ERR_CANCELED" ||
          e?.name === "CanceledError"
        ) {
          return;
        }
        if (e instanceof AnalyticsForbiddenError || e?.isForbidden) {
          setError(true);
          setStats(null);
        } else {
          setError(true);
          setStats(null);
        }
      } finally {
        if (!signal.aborted && !ignore) {
          setLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [active, companyId, debugRange.from, debugRange.to]);

  return {
    loading: active ? loading : false,
    error: active ? error : false,
    stats: active ? stats : null,
    period: active ? debugRange : null,
    periodHint,
    canView,
  };
}
